/*
 * DelayAlert
 *
 * A component that checks and shows the delay alert info of which banks
 * that currently experiencing minor or major delay.
 */

import { useTranslation } from "react-i18next";

import { faExclamationTriangle, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { Alert } from "src/components-v2";

import { STATUS_DELAY } from "src/consts/delay";
import { HTMLAttributes } from "react";

type DelayAlertProps = Pick<HTMLAttributes<HTMLDivElement>, "className"> & {
  /**
   * Current bank key that has been selected
   */
  selectedBank?: string;

  /**
   * Current bank label that has been selected
   */
  selectedBankLabel?: string;

  /**
   * An object consists of bank keys and delay status
   * eg:
   *  {
   *    mandiri: {
   *      key: "mandiri",
   *      status: "2"
   *    },
   *    bca: {
   *      key: "bca",
   *      status: "3"
   *    }
   *  }
   */
  bankStatus: {
    [bankKey: string]: {
      key: string;
      status: string;
    };
  };

  /**
   * Current maintenance status
   */
  maintenanceStatus: {
    is_maintenance: boolean;
    end: string;
  };
};

const defaultProps = {
  selectedBank: "",
  selectedBankLabel: "",
  bankStatus: {},
  maintenanceStatus: {}
};

const DELAY_TYPES = {
  [STATUS_DELAY.MINOR]: "minor",
  [STATUS_DELAY.MAJOR]: "major"
};

const ALERT_COLORS = {
  [STATUS_DELAY.MINOR]: "yellow",
  [STATUS_DELAY.MAJOR]: "red"
} as const;

function DelayAlert(props: DelayAlertProps) {
  const { selectedBank, selectedBankLabel, bankStatus, maintenanceStatus, ...restProps } = props;
  const { t } = useTranslation("translation");

  const isDelayed = [STATUS_DELAY.MINOR, STATUS_DELAY.MAJOR].includes(bankStatus[selectedBank!]?.status);
  if (!isDelayed && !maintenanceStatus?.is_maintenance) return null;

  const status = bankStatus[selectedBank]?.status;
  const delayType = DELAY_TYPES[status];
  const alertColor = ALERT_COLORS[status];

  return maintenanceStatus?.is_maintenance ? (
    <Alert color="grey" icon={faInfoCircle} {...restProps}>
      {t("bank-delay.info-maintenance", { bank: selectedBankLabel, end: maintenanceStatus?.end })}
    </Alert>
  ) : (
    <Alert color={alertColor} icon={faExclamationTriangle} {...restProps}>
      <div>
        {t(`bank-delay.info-${delayType}.delay`)}
        <b>{t(`bank-delay.info-${delayType}.delay-bold`)}</b>
        {t(`bank-delay.info-${delayType}.delay-cont`)}
      </div>
    </Alert>
  );
}

DelayAlert.defaultProps = defaultProps;

export default DelayAlert;
